// components/EntityManager/EntityManagerPage.jsx
import React, { useState } from "react";
import {
  Text, Button, Badge, Switch, Tooltip,
  Dialog, DialogSurface, DialogTitle, DialogBody, DialogActions,
  SearchBox, Select,
} from "@fluentui/react-components";
import {
  Add24Regular, Delete24Regular, Edit24Regular,
  ArrowSync24Regular, Warning24Regular,
} from "@fluentui/react-icons";
import { useSchema } from "../../context/SchemaContext";
import EntityFormDialog from "./EntityFormDialog";
import { GRADIENT_OPTIONS, FIELD_TYPES } from "../../hooks/useEntitySchema";

function GradientDot({ gradient }) {
  const g = GRADIENT_OPTIONS.find((o) => o.key === gradient);
  return (
    <div
      style={{
        width: 14, height: 14, borderRadius: "50%",
        background: g?.css || "#ccc", flexShrink: 0,
        boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
      }}
    />
  );
}

function CategorySection({ category, entities, onEdit, onDelete, onToggle, onMoveUp, onMoveDown, allEntities }) {
  return (
    <div style={{ marginBottom: 24 }}>
      <div
        style={{
          display: "flex", alignItems: "center", gap: 10,
          marginBottom: 10, padding: "8px 12px",
          background: "linear-gradient(90deg, #001A3D, #002D7A)",
          borderRadius: 10,
        }}
      >
        <Text weight="bold" style={{ color: "#fff", fontSize: 13 }}>{category}</Text>
        <Badge appearance="tint" style={{ background: "rgba(255,255,255,0.2)", color: "#fff", fontSize: 10 }}>
          {entities.length} entities
        </Badge>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {entities.map((entity, idx) => {
          const globalIdx = allEntities.findIndex((e) => e.id === entity.id);
          return (
            <div
              key={entity.id}
              style={{
                display: "flex", alignItems: "center", gap: 12,
                background: entity.active ? "#fff" : "#F8F7F6",
                border: `1px solid ${entity.active ? "#EDEBE9" : "#C8C6C4"}`,
                borderRadius: 10, padding: "10px 14px",
                opacity: entity.active ? 1 : 0.65,
                transition: "all 0.15s",
              }}
            >
              {/* Drag handle */}
              <div style={{ cursor: "grab", color: "#C8C6C4", flexShrink: 0, display: "flex", alignItems: "center" }}>
                <span style={{ fontSize: 14, letterSpacing: 2 }}>⋮⋮</span>
              </div>

              {/* Icon + gradient dot */}
              <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                <span style={{ fontSize: 20 }}>{entity.icon}</span>
                <GradientDot gradient={entity.cardGradient} />
              </div>

              {/* Info */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <Text weight="semibold" style={{ fontSize: 13, color: "#201F1E" }}>{entity.name}</Text>
                  <Badge appearance="outline" style={{ fontSize: 10 }}>{FIELD_TYPES[entity.type]?.label || entity.type}</Badge>
                  {entity.unit && <Badge appearance="tint" color="subtle" style={{ fontSize: 10 }}>{entity.unit}</Badge>}
                  {entity.showOnDashboard && <Badge appearance="tint" color="success" style={{ fontSize: 10 }}>Dashboard</Badge>}
                  {entity.showInChart && <Badge appearance="tint" color="informative" style={{ fontSize: 10 }}>Charts</Badge>}
                </div>
                <Text style={{ fontSize: 11, color: "#8A8886", fontFamily: "monospace" }}>key: {entity.key}</Text>
              </div>

              {/* Active toggle */}
              <Switch
                checked={entity.active}
                onChange={() => onToggle(entity.id)}
                label={<Text style={{ fontSize: 11, color: "#605E5C" }}>{entity.active ? "Active" : "Hidden"}</Text>}
              />

              {/* Actions */}
              <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
                <Tooltip content="Move Up" relationship="label">
                  <Button
                    size="small" appearance="subtle"
                    icon={<span style={{ transform: "rotate(0deg)", display: "block" }}>↑</span>}
                    onClick={() => onMoveUp(globalIdx)}
                    disabled={globalIdx === 0}
                  />
                </Tooltip>
                <Tooltip content="Move Down" relationship="label">
                  <Button
                    size="small" appearance="subtle"
                    icon={<span>↓</span>}
                    onClick={() => onMoveDown(globalIdx)}
                    disabled={globalIdx === allEntities.length - 1}
                  />
                </Tooltip>
                <Tooltip content="Edit" relationship="label">
                  <Button size="small" appearance="subtle" icon={<Edit24Regular />} onClick={() => onEdit(entity)} />
                </Tooltip>
                <Tooltip content="Delete" relationship="label">
                  <Button
                    size="small" appearance="subtle"
                    icon={<Delete24Regular style={{ color: "#D13438" }} />}
                    onClick={() => onDelete(entity)}
                  />
                </Tooltip>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function EntityManagerPage() {
  const {
    schema, addEntity, updateEntity, removeEntity,
    toggleActive, reorder, resetToDefault,
  } = useSchema();

  const [addOpen, setAddOpen] = useState(false);
  const [editEntity, setEditEntity] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [resetOpen, setResetOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [filterCat, setFilterCat] = useState("all");

  const handleSave = (data) => {
    if (editEntity) {
      updateEntity(editEntity.id, data);
      setEditEntity(null);
    } else {
      addEntity(data);
      setAddOpen(false);
    }
  };

  const handleDelete = () => {
    removeEntity(deleteTarget.id);
    setDeleteTarget(null);
  };

  const handleMoveUp = (idx) => { if (idx > 0) reorder(idx, idx - 1); };
  const handleMoveDown = (idx) => { if (idx < schema.length - 1) reorder(idx, idx + 1); };

  // Filter schema for display
  const filteredSchema = schema.filter((e) => {
    const matchSearch = !search || e.name.toLowerCase().includes(search.toLowerCase()) || e.key.includes(search.toLowerCase());
    const matchCat = filterCat === "all" || e.category === filterCat;
    return matchSearch && matchCat;
  });

  const filteredByCategory = {};
  filteredSchema.forEach((e) => {
    if (!filteredByCategory[e.category]) filteredByCategory[e.category] = [];
    filteredByCategory[e.category].push(e);
  });

  const allCategories = [...new Set(schema.map((e) => e.category))];
  const activeCount = schema.filter((e) => e.active).length;
  const dashboardCount = schema.filter((e) => e.active && e.showOnDashboard).length;
  const chartCount = schema.filter((e) => e.active && e.showInChart).length;

  return (
    <div>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 24 }}>
        <div>
          <Text weight="bold" size={600} style={{ color: "#201F1E", display: "block" }}>
            Entity Manager
          </Text>
          <Text style={{ color: "#605E5C", fontSize: 13 }}>
            Define which metrics to track. Changes apply instantly across all dashboard views.
          </Text>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <Button
            appearance="outline"
            icon={<ArrowSync24Regular />}
            onClick={() => setResetOpen(true)}
            style={{ color: "#D13438", borderColor: "#D13438" }}
          >
            Reset Defaults
          </Button>
          <Button
            appearance="primary"
            icon={<Add24Regular />}
            onClick={() => setAddOpen(true)}
            style={{ background: "#0052CC" }}
          >
            Add Entity
          </Button>
        </div>
      </div>

      {/* Summary stats */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 24 }}>
        {[
          { label: "Total Entities", value: schema.length, color: "#0052CC" },
          { label: "Active", value: activeCount, color: "#107C10" },
          { label: "On Dashboard", value: dashboardCount, color: "#5C2D91" },
          { label: "In Charts", value: chartCount, color: "#008272" },
        ].map((s, i) => (
          <div
            key={i}
            style={{
              background: "#fff", borderRadius: 12, padding: "14px 18px",
              border: "1px solid #EDEBE9", borderLeft: `4px solid ${s.color}`,
              boxShadow: "0 1px 6px rgba(0,0,0,0.05)",
            }}
          >
            <Text style={{ fontSize: 11, color: "#8A8886", textTransform: "uppercase", letterSpacing: 0.6, display: "block" }}>{s.label}</Text>
            <Text weight="bold" style={{ fontSize: 24, color: s.color }}>{s.value}</Text>
          </div>
        ))}
      </div>

      {/* Info banner */}
      <div style={{
        background: "linear-gradient(90deg, #EBF3FF, #F5EDFF)",
        border: "1px solid #C7E0FF", borderRadius: 10,
        padding: "12px 16px", marginBottom: 20,
        display: "flex", alignItems: "center", gap: 12,
      }}>
        <span style={{ fontSize: 20 }}>💡</span>
        <Text style={{ fontSize: 13, color: "#3A3A3A" }}>
          <strong>How it works:</strong> Toggle entities on/off anytime. Add brand-new metrics with custom types, categories, and chart styles. Historical data is preserved — new entities just show "—" for past weeks where data wasn't collected.
        </Text>
      </div>

      {/* Filters */}
      <div style={{ display: "flex", gap: 12, marginBottom: 20, alignItems: "center" }}>
        <SearchBox
          placeholder="Search entities..."
          value={search}
          onChange={(_, d) => setSearch(d.value)}
          style={{ width: 260 }}
        />
        <Select value={filterCat} onChange={(e) => setFilterCat(e.target.value)} style={{ width: 220 }}>
          <option value="all">All Categories</option>
          {allCategories.map((c) => <option key={c} value={c}>{c}</option>)}
        </Select>
        {(search || filterCat !== "all") && (
          <Button appearance="subtle" onClick={() => { setSearch(""); setFilterCat("all"); }}>Clear filters</Button>
        )}
        <Text style={{ fontSize: 12, color: "#8A8886", marginLeft: "auto" }}>
          Showing {filteredSchema.length} of {schema.length} entities
        </Text>
      </div>

      {/* Category groups */}
      {Object.keys(filteredByCategory).length === 0 ? (
        <div style={{ background: "#fff", borderRadius: 16, padding: 48, textAlign: "center", border: "1px solid #EDEBE9" }}>
          <Text style={{ color: "#8A8886", fontSize: 15 }}>No entities match your filter</Text>
        </div>
      ) : (
        Object.entries(filteredByCategory).map(([cat, entities]) => (
          <CategorySection
            key={cat}
            category={cat}
            entities={entities}
            allEntities={schema}
            onEdit={setEditEntity}
            onDelete={setDeleteTarget}
            onToggle={toggleActive}
            onMoveUp={handleMoveUp}
            onMoveDown={handleMoveDown}
          />
        ))
      )}

      {/* Add / Edit dialog */}
      <EntityFormDialog
        open={addOpen || !!editEntity}
        onClose={() => { setAddOpen(false); setEditEntity(null); }}
        onSave={handleSave}
        editEntity={editEntity}
        existingCategories={allCategories}
      />

      {/* Delete confirm */}
      <Dialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <DialogSurface style={{ maxWidth: 420 }}>
          <DialogTitle>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <Warning24Regular style={{ color: "#D13438", fontSize: 24 }} />
              <Text weight="bold" style={{ color: "#201F1E" }}>Delete Entity</Text>
            </div>
          </DialogTitle>
          <DialogBody>
            <Text style={{ color: "#323130" }}>
              Delete <strong>"{deleteTarget?.name}"</strong>? This removes it from all views. Historical record values for this field are NOT deleted from storage, but will no longer be visible.
            </Text>
            <div style={{ background: "#FFF4CE", border: "1px solid #FFD335", borderRadius: 8, padding: "10px 14px", marginTop: 12 }}>
              <Text style={{ fontSize: 12, color: "#7A5500" }}>
                💡 Tip: Consider toggling it <strong>Inactive</strong> instead of deleting, to preserve the option to re-enable it later.
              </Text>
            </div>
          </DialogBody>
          <DialogActions>
            <Button appearance="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button appearance="primary" style={{ background: "#D13438" }} onClick={handleDelete}>Delete Entity</Button>
          </DialogActions>
        </DialogSurface>
      </Dialog>

      {/* Reset confirm */}
      <Dialog open={resetOpen} onOpenChange={() => setResetOpen(false)}>
        <DialogSurface style={{ maxWidth: 420 }}>
          <DialogTitle>Reset to Defaults</DialogTitle>
          <DialogBody>
            <Text>This will restore the original entity schema from your Excel data. Any custom entities you've added will be removed.</Text>
          </DialogBody>
          <DialogActions>
            <Button appearance="outline" onClick={() => setResetOpen(false)}>Cancel</Button>
            <Button appearance="primary" style={{ background: "#D13438" }} onClick={() => { resetToDefault(); setResetOpen(false); }}>Reset</Button>
          </DialogActions>
        </DialogSurface>
      </Dialog>
    </div>
  );
}
